/**
 * Given a source object, will take all the fields which are defined and set them unto the target object.
 * Main use-case is updating slice data with protobuf provided payloads as we use the presence of the fields.
 *
 * @param target object on which to set the fields
 * @param source payload with the desired update
 */

// Utility type for deep partial - makes all nested properties optional
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object
    ? T[P] extends Array<infer U>
      ? Array<DeepPartial<U>>
      : DeepPartial<T[P]>
    : T[P];
};

export function assignDefinedFields<T extends object>(
  target: T,
  source: DeepPartial<T>,
): T {
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const srcValue = source[key];
      if (srcValue !== undefined) {
        const tgtValue = (target as Record<string, unknown>)[key];
        if (isObject(srcValue) && isObject(tgtValue)) {
          // Recursively assign nested objects
          (target as Record<string, unknown>)[key] = assignDefinedFields(
            { ...tgtValue },
            {
              ...srcValue,
            } as DeepPartial<typeof tgtValue>,
          );
        } else {
          // Overwrite primitive values, arrays, or when target isn't an object
          (target as Record<string, unknown>)[key] = srcValue;
        }
      }
    }
  }

  return target;
}

function isObject(item: unknown): item is object {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
}
